import type { NativeKktDocument } from '@core/native/kkt';
import type { PosShift } from '@core/db/models';

/** Состояние смены, полученное из ККТ. */
export interface KktShiftState {
  connected?: boolean;
  state?: 'ok' | 'paper_out' | 'shift_closed' | 'error' | 'no_device';
  interface?: 'usb' | 'com' | 'tcp' | 'emulator';
  shiftOpen?: boolean;
  shiftNumber?: number;
}

export type KktShiftMismatch =
  | 'kkt_open_pos_closed'
  | 'kkt_closed_pos_open'
  | 'shift_number_mismatch';

/**
 * Сверка локальной смены POSPoint с фискальной сменой ККТ.
 *
 * KKT — источник истины для фискальной смены; локальная смена хранит
 * бизнес-контекст POS. Несовпадение должно блокировать фискальные операции,
 * а не открывать вторую смену вслепую.
 */
export function detectKktShiftMismatch(
  local: Pick<PosShift, 'status' | 'shiftNumber'> | null,
  kkt: KktShiftState | null | undefined
): KktShiftMismatch | null {
  if (!kkt || kkt.connected === false || kkt.state === 'no_device') return null;
  // Эмулятор не является фискальной сменой; его состояние не блокирует POS.
  if (kkt.interface === 'emulator') return null;
  if (typeof kkt.shiftOpen !== 'boolean') return null;

  const localOpen = local?.status === 'open';
  if (kkt.shiftOpen && !localOpen) return 'kkt_open_pos_closed';
  if (!kkt.shiftOpen && localOpen) return 'kkt_closed_pos_open';

  if (
    kkt.shiftOpen &&
    localOpen &&
    typeof kkt.shiftNumber === 'number' &&
    kkt.shiftNumber > 0 &&
    local?.shiftNumber != null &&
    kkt.shiftNumber !== local.shiftNumber
  ) {
    return 'shift_number_mismatch';
  }
  return null;
}

export interface KktDocumentsState {
  documentCount?: number;
  externalReceipts?: number;
  externalDocumentNumbers?: number[];
  lastDocNumber?: number;
  unknownDocuments?: number;
}

export interface ReconcileKktDocumentsInput {
  shiftNumber: number;
  documents: NativeKktDocument[];
  localFiscalNumbers: Iterable<number>;
  incremental: boolean;
  complete: boolean;
  previous: KktDocumentsState;
}

export interface ReconcileKktDocumentsResult extends KktDocumentsState {
  zComplete: boolean;
  syncError?: string;
}

const RECEIPT_TYPES = new Set(['receipt', 'receiptCorrection', 'bso', 'bsoCorrection']);
const NON_RECEIPT_TYPES = new Set([
  'openShift',
  'closeShift',
  'registration',
  'changeRegistrationParameters',
  'closeArchive',
  'ofdExchangeStatus'
]);

/**
 * Пересчитать состояние сверки по прочитанным документам.
 *
 * `complete=false` не двигает lastDocNumber: иначе документ, на котором
 * произошла ошибка, навсегда выпадет из следующего инкрементального чтения.
 * Внешние ФД хранятся номерами, поэтому счётчик уменьшается, когда локальный
 * чек позже получает fiscalData (offline_pending → fiscalized).
 */
export function reconcileKktDocuments(
  input: ReconcileKktDocumentsInput
): ReconcileKktDocumentsResult {
  if (!input.complete) {
    return {
      ...input.previous,
      zComplete: false,
      syncError: 'documents_incomplete'
    };
  }

  const localFiscalNumbers = new Set(input.localFiscalNumbers);
  const documentCount = input.incremental ? (input.previous.documentCount ?? 0) : 0;
  const lastDocNumber = input.incremental ? (input.previous.lastDocNumber ?? 0) : 0;
  let unknownDocuments = input.incremental ? (input.previous.unknownDocuments ?? 0) : 0;
  const externalDocumentNumbers = new Set(
    input.incremental ? (input.previous.externalDocumentNumbers ?? []) : []
  );

  // Локальный чек мог стать фискальным уже после первой сверки.
  for (const number of localFiscalNumbers) externalDocumentNumbers.delete(number);

  let nextCount = documentCount;
  let nextLastDocNumber = lastDocNumber;
  for (const doc of input.documents) {
    if (doc.shiftNumber != null && doc.shiftNumber !== input.shiftNumber) continue;
    if (input.incremental && doc.fiscalDocumentNumber <= lastDocNumber) continue;
    nextCount += 1;
    nextLastDocNumber = Math.max(nextLastDocNumber, doc.fiscalDocumentNumber);
    const type = doc.documentType ?? '';
    if (RECEIPT_TYPES.has(type)) {
      if (!localFiscalNumbers.has(doc.fiscalDocumentNumber)) {
        externalDocumentNumbers.add(doc.fiscalDocumentNumber);
      }
    } else if (!NON_RECEIPT_TYPES.has(type)) {
      unknownDocuments += 1;
    }
  }

  const externalReceipts = externalDocumentNumbers.size;
  return {
    documentCount: nextCount,
    externalReceipts,
    externalDocumentNumbers: [...externalDocumentNumbers],
    lastDocNumber: nextLastDocNumber || undefined,
    unknownDocuments,
    zComplete: externalReceipts === 0 && unknownDocuments === 0
  };
}

export function kktShiftMismatchText(mismatch: KktShiftMismatch): string {
  switch (mismatch) {
    case 'kkt_open_pos_closed':
      return 'Смена открыта в ККТ, но не открыта в POSPoint';
    case 'kkt_closed_pos_open':
      return 'Смена закрыта в ККТ, но открыта в POSPoint';
    case 'shift_number_mismatch':
      return 'Номер смены POSPoint не совпадает с номером смены ККТ';
  }
}
